import React, { useCallback, useContext, useEffect, useRef } from "react";
import useHttp from "../hooks/useHttp";
import { initPusher } from "../pages/utils/CommonVessels";
import { AppContext } from "./AppContext";

const PusherContext = React.createContext();
export const usePusher = () => useContext(PusherContext);

export const PusherProvider = React.memo(({ children }) => {
  const pusherRefOrg = useRef(null);
  const channelRefOrg = useRef(null);

  const pusherRefUser = useRef(null);
  const channelRefUser = useRef(null);

  const [appProperties, setAppProperties] = useContext(AppContext);
  const connectionProperties = useRef({});
  const { fetchData } = useHttp();

  const connect = useCallback(
    async (subscribeToOrg = false, subscribeToUser = false) => {
      try {
        if (!subscribeToOrg && !subscribeToUser) return false;

        if (subscribeToOrg && !connectionProperties.current.org) {
          const pusherInitResponse = await initPusher(fetchData, appProperties);
          if (pusherInitResponse) {
            pusherRefOrg.current = pusherInitResponse;
            channelRefOrg.current = pusherInitResponse?.channel;
            connectionProperties.current.org = true;
          }
        }

        if (subscribeToUser && !connectionProperties.current.user) {
          const pusherInitUserResponse = await initPusher(fetchData, appProperties, appProperties?.currentUser?.osyncUserId);
          if (pusherInitUserResponse) {
            pusherRefUser.current = pusherInitUserResponse;
            channelRefUser.current = pusherInitUserResponse?.channel;
          }
          connectionProperties.current.user = true;
        }
        return true;
      } catch (error) {
        console.error("Something went wrong while connecting to Pusher", error);
        return false;
      } finally {
        if (appProperties?.savedNumbers?.some((number) => number?.serviceName === "unipile_whatsapp")) {
          subscribe(
            "WHATSAPP_DAILY_LIMIT_EXCEEDED",
            (res) => {
              if (res?.data) {
                var phoneNumber = JSON.parse(res?.data);
                setAppProperties((prev) => {
                  if (!prev?.savedNumbers) return prev;

                  const updatedNumbers = prev.savedNumbers.map((number) => {
                    if (number?.phoneNumber === phoneNumber && !number?.showDailyLimitReachedWarning) {
                      return { ...number, showDailyLimitReachedWarning: true };
                    }
                    return number;
                  });

                  return {
                    ...prev,
                    savedNumbers: updatedNumbers,
                  };
                });
              }
            },
            false,
            true
          );
        }
      }
    },
    [appProperties, fetchData]
  );

  const subscribe = useCallback((channelName, callBackFn, isOrgSub = true, isUserSub = false) => {
    if (!channelRefOrg.current && !channelRefUser.current) {
      console.warn("Called subscribe before properly connected to Pusher");
      return;
    }
    if (!connectionProperties.current.subscribedChannels) {
      connectionProperties.current.subscribedChannels = [];
    }

    if (!connectionProperties.current.subscribedChannels?.includes(channelName)) {
      connectionProperties.current.subscribedChannels.push(channelName);
      if (isOrgSub) {
        channelRefOrg.current?.bind(channelName, callBackFn);
      } else if (isUserSub) {
        channelRefUser.current?.bind(channelName, callBackFn);
      }
    }
  }, []);

  const unSubscribe = useCallback((channelName, caller) => {
    channelRefOrg.current?.unbind(channelName);
    if (connectionProperties.current.subscribedChannels && connectionProperties.current.subscribedChannels?.length > 0) {
      connectionProperties.current.subscribedChannels = connectionProperties.current.subscribedChannels.filter(
        (channel) => channel !== channelName
      );
    }
  }, []);

  const value = {
    pusherRefOrg: pusherRefOrg.current,
    channelRefOrg: channelRefOrg.current,
    pusherRefUser: pusherRefUser.current,
    channelRefUser: channelRefUser.current,
    subscribe,
    unSubscribe,
    connect,
    connectionProperties: connectionProperties.current,
  };

  useEffect(() => {
    return () => {
      connectionProperties.current.user = false;
      connectionProperties.current.org = false;
    };
  }, []);

  return <PusherContext.Provider value={value}>{children}</PusherContext.Provider>;
});
